import type { IconName } from '../components/Icon'
import { linkTone, unpackedTone, whenPill, type CardTone, type WhenPill } from '../components/cardKind'
import { urlDisplayTitle, type Note, type UrlEntity } from '../domain/note'
import { hasSpecificLinkImage } from '../links/linkMetadata'
import { linkPreviewPresentation } from '../links/linkPreviewCategory'
import { sameSubject } from '../links/mergeUnpacked'
import { cardIcon, type UnpackedCard } from '../screenshotCleanup/card'
import { hideMarkdownPreviewArtifacts } from '../utils/markdownText'
import type { BrowseCardModel } from './browseCardModel'
import type { SearchLayout } from './layoutPreference'

/**
 * The one Browse card (apps/app/DESIGN.md, "Browse card"): five slots in a fixed order — media, kind, title, body, footer — whatever the
 * note holds. This decides what fills each slot; layouts change only how much shows. A link that is the note's subject is the card itself,
 * never a card inside it; other links are quiet rows. A picture is first or absent.
 */
export type CardMedia =
  | { type: 'pictures' }
  | { type: 'link'; url: UrlEntity }
  | { type: 'route'; route: NonNullable<UnpackedCard['route']> }

export interface CardKind {
  icon: IconName
  tone: CardTone
  word: string | null
  source: string | null
  when: WhenPill | null
}

export interface CardRecipe {
  media: CardMedia | null
  kind: CardKind | null
  title: string | null
  quote: boolean
  /** A booking's day and hour (with its venue), in the kind's hue. */
  line: string | null
  body: { type: 'markdown'; markdown: string } | { type: 'text'; text: string } | null
  /** Links that are not the card's subject, as rows. */
  links: UrlEntity[]
  moreLinks: number
  /** The link the card is: its title, picture and kind lead the card. */
  subject: UrlEntity | null
}

const SEPARATOR = /\s+[|·–—-]\s+/

/** A page title without the site name it carries at either end ("… | CNN Business", "GitHub - owner/repo: …"). */
export function linkCardTitle(url: UrlEntity, provider: string): string {
  const title = hideMarkdownPreviewArtifacts(urlDisplayTitle(url)).trim()
  const site = provider.trim().toLowerCase()
  if (!site) return title
  const parts = title.split(SEPARATOR)
  if (parts.length < 2) return title
  const named = (part: string) => { const value = part.trim().toLowerCase(); return value === site || value.startsWith(site) || site.startsWith(value) }
  if (named(parts.at(-1)!)) return title.slice(0, title.lastIndexOf(parts.at(-1)!)).replace(SEPARATOR, '').replace(/\s+[|·–—-]\s*$/, '').trim() || title
  if (named(parts[0]!)) return title.slice(parts[0]!.length).replace(/^\s*[|·–—-]\s+/, '').trim() || title
  return title
}

const sameText = (a: string | null | undefined, b: string | null | undefined) => {
  const clean = (value: string | null | undefined) => (value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
  const left = clean(a), right = clean(b)
  return Boolean(left && right) && (left.startsWith(right.slice(0, 60)) || right.startsWith(left.slice(0, 60)))
}

/** The note's words are only what its unpacked screenshots or shared message already say (shared into the note): not words of its own. */
export function echoesUnpacked(markdown: string, words: string): boolean {
  const squash = (text: string) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
  const mine = squash(markdown).slice(0, 80)
  if (mine && squash(words).includes(mine)) return true
  // A shared message reworded into fields ("When: Wednesday, 28 October at 2:00pm"): most of its words are the reading's.
  const tokens = (text: string) => text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(word => word.length >= 3)
  const theirs = new Set(tokens(words))
  const own = tokens(markdown).slice(0, 40)
  return own.length >= 3 && own.filter(word => theirs.has(word)).length / own.length >= 0.6
}

/** An unpacked item's kind line: its glyph, word, where it came from (when asked) and how soon. Saved text has none. */
export function unpackedKind(card: UnpackedCard, now: number, withSource = true): CardKind | null {
  if (card.plain || card.kind === 'other') return null
  return {
    icon: cardIcon(card), tone: unpackedTone(card), word: card.label,
    source: withSource && (card.kind === 'post' || card.kind === 'recipe' || card.kind === 'article') ? card.detail ?? null : null,
    when: whenPill(card.startsAt, now),
  }
}

export function cardRecipe({ note, model, layout, unpacked, pictures, pictureCount, maxLinks, now, failedImages }: {
  note: Pick<Note, 'urls' | 'manualTitle' | 'contentType' | 'annotation'>
  model: Pick<BrowseCardModel, 'hasAuthoredBody' | 'previewMarkdown' | 'shouldShowNoteTitle' | 'visibleTitle'>
  layout: SearchLayout
  unpacked: UnpackedCard | null
  /** The note shows a picture of its own (its images, or Unpack's cover of them). */
  pictures: boolean
  pictureCount: number
  maxLinks: number
  now: number
  /** Link pictures that failed to load here: the card is laid out as if they had none. */
  failedImages?: ReadonlySet<string>
}): CardRecipe {
  const urls = note.urls
  const card = unpacked && !unpacked.plain && unpacked.kind !== 'other' ? unpacked : null
  // Your words: the note's own text, or the annotation you wrote for its links.
  const authored = (model.hasAuthoredBody || note.annotation.trim()) && model.previewMarkdown.trim() ? model.previewMarkdown : ''
  const own = authored && !(unpacked && echoesUnpacked(authored, unpacked.words)) ? authored : ''
  const hasImage = (url: UrlEntity) => hasSpecificLinkImage(url, url.imageUrl) && !(url.imageUrl && failedImages?.has(url.imageUrl))
  const merged = Boolean(card && urls.length === 1 && sameSubject(urls[0]!, card))
  // A note that is its one link (alone, or under the page's own name as its title) has that link as its subject.
  const named = urls.length === 1 && [linkCardTitle(urls[0]!, linkPreviewPresentation(urls[0]!, note.contentType).provider), urlDisplayTitle(urls[0]!)].some(name => sameText(name, model.visibleTitle))
  const linkOnly = !card && urls.length === 1 && !pictures && (!own || named)
  const subject = merged || linkOnly ? urls[0]! : null
  const others = urls.filter(url => url !== subject)
  const presentation = subject ? linkPreviewPresentation(subject, note.contentType) : null
  const manual = note.manualTitle?.trim() || null

  let kind: CardKind | null = null
  let title: string | null = null
  let line: string | null = null
  let quote = false
  let body: CardRecipe['body'] = own ? { type: 'markdown', markdown: own } : null

  if (card) {
    const own = unpackedKind(card, now)!
    kind = { ...own, source: own.source ?? (card.kind === 'post' || card.kind === 'recipe' || card.kind === 'article' ? presentation?.provider ?? null : null) }
    if (card.kind === 'quote') { quote = true; title = card.title; line = card.detail }
    else if (card.route) {
      title = card.route.from.code && card.route.to.code ? `${card.route.from.code} → ${card.route.to.code}` : `${card.route.from.place ?? ''} → ${card.route.to.place ?? ''}`
      line = card.heading ?? null
    } else {
      title = card.heading ?? card.title
      line = card.line ?? null
    }
    if (!body && (card.kind === 'post' || card.kind === 'article') && card.excerpt) body = { type: 'text', text: card.excerpt }
  } else if (subject && presentation) {
    const plainLink = presentation.category === 'link'
    kind = {
      icon: presentation.icon, tone: linkTone(presentation.category),
      word: plainLink ? presentation.provider : presentation.label,
      source: plainLink ? subject.author ?? null : [presentation.provider, subject.author].filter(Boolean).join(' · ') || null,
      when: null,
    }
    title = linkCardTitle(subject, presentation.provider)
    const description = subject.description ? hideMarkdownPreviewArtifacts(subject.description).trim() : ''
    if (!body && description && !sameText(description, title)) body = { type: 'text', text: description }
  } else if (urls.length > 1 && !own) {
    kind = { icon: 'link', tone: 'plain', word: `${urls.length} links`, source: null, when: null }
  } else if (pictures && pictureCount > 0) {
    kind = { icon: 'image', tone: 'photo', word: pictureCount > 1 ? `${pictureCount} photos` : 'Photo', source: null, when: null }
  }
  if (manual) title = manual
  // A note of several links and no words: the rows are its content, and a derived title would only repeat the first of them.
  else if (!title && urls.length > 1 && !own && layout !== 'minimal' && maxLinks > 0) title = null
  else if (!title && model.shouldShowNoteTitle) title = model.visibleTitle || null

  // Media: the note's own picture, then the subject link's, then a link's beside your words; a route for a trip.
  let media: CardMedia | null = null
  if (card?.route) media = { type: 'route', route: card.route }
  else if (pictures) media = { type: 'pictures' }
  else if (subject && hasImage(subject)) media = { type: 'link', url: subject }
  else if (!card && (others.length === 1 || !own)) {
    // One link beside your words lends its picture; among several, only a note of links alone takes the first picture.
    const pictured = others.find(hasImage)
    if (pictured) media = { type: 'link', url: pictured }
  }
  const wordless = !title && !body
  if (layout === 'minimal' && !wordless) media = null
  if (layout !== 'detailed' && media?.type === 'route') media = null
  // With its route on the cover, a trip's title is its day and hour.
  if (media?.type === 'route') { title = line ?? title; line = null }
  // A compact tile under a picture keeps to its name.
  if (layout === 'compact' && media && media.type !== 'route') body = null

  const shown = layout === 'minimal' ? 0 : Math.max(0, maxLinks)
  return { media, kind, title, quote, line, body, links: others.slice(0, shown), moreLinks: Math.max(0, others.length - shown), subject }
}
